/* <jump-menu-section>. IIFE-guarded because this asset can load twice on one page. */
(() => {
  if (customElements.get('jump-menu-section')) return;

class JumpMenuSection extends HTMLElement {
  constructor() {
    super();

    this.items = [];
    this.openItem = null;
    this.scrollRaf = null;
    this.desktopMedia = window.matchMedia('screen and (min-width: 1024px)');
    this.facetUpdateUnsubscriber = undefined;
    this.activeFamilyValue = null;
    this.collectionFetchController = null;

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onDesktopChange = this.onDesktopChange.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onFilterChange = this.onFilterChange.bind(this);
    this.onSelectAllChange = this.onSelectAllChange.bind(this);
    this.onFilterFamilyClick = this.onFilterFamilyClick.bind(this);
    this.onFacetUpdate = this.onFacetUpdate.bind(this);
    this.onPopState = this.onPopState.bind(this);

    this.tileSlider = null;
    this.drag = null;
    this.suppressDragClick = false;
    this.onDragPointerDown = this.onDragPointerDown.bind(this);
    this.onDragPointerMove = this.onDragPointerMove.bind(this);
    this.onDragPointerUp = this.onDragPointerUp.bind(this);
    this.onDragNativeStart = this.onDragNativeStart.bind(this);
    this.onDragClickCapture = this.onDragClickCapture.bind(this);
    this.updateDragAffordance = this.updateDragAffordance.bind(this);
  }

  get isFilterMode() {
    return this.dataset.mode === 'collection_filters' && Boolean(this.dataset.colorFilterParam);
  }

  get colorFilterParam() {
    return this.dataset.colorFilterParam || '';
  }

  get colorFamilyFilterParam() {
    return this.dataset.colorFamilyFilterParam || '';
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.jump-menu__item[data-has-submenu]'));

    this.querySelectorAll('.jump-menu__item').forEach((item) => {
      const submenu = item.querySelector('.jump-menu__submenu');
      const itemTrigger = item.querySelector('.jump-menu__item-link');

      if (item.hasAttribute('data-has-submenu')) {
        item.addEventListener('pointerenter', () => this.onItemPointerEnter(item));

        if (submenu) {
          submenu.addEventListener('keydown', (event) => this.onSubmenuKeydown(event, item));
        }
      }

      if (itemTrigger) {
        itemTrigger.addEventListener('click', (event) => this.onItemTriggerClick(event, item));
        itemTrigger.addEventListener('keydown', (event) => this.onItemTriggerKeydown(event, item));
      }

      if (this.isFilterMode) {
        const selectAll = item.querySelector('[data-jump-menu-select-all]');
        if (selectAll) {
          selectAll.addEventListener('change', this.onSelectAllChange);
        }

        this.getColorInputs(item).forEach((input) => {
          input.addEventListener('change', this.onFilterChange);
        });
      }
    });

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
    this.desktopMedia.addEventListener('change', this.onDesktopChange);
    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.isFilterMode) {
      this.facetUpdateUnsubscriber = theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.facetUpdate,
        this.onFacetUpdate
      );
      this.syncFromUrl();
      window.addEventListener('popstate', this.onPopState);
      this.logFilterDebug('connected', {
        collectionSectionId: this.getCollectionSectionId()
      });
    }

    this.setupTileDrag();

    if (Shopify.designMode) {
      document.addEventListener('shopify:block:select', this.onBlockSelect.bind(this));
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    this.desktopMedia.removeEventListener('change', this.onDesktopChange);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
    this.teardownTileDrag();
    this.clearTimers();

    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
      this.facetUpdateUnsubscriber = undefined;
    }

    if (this.isFilterMode) {
      window.removeEventListener('popstate', this.onPopState);
    }

    this.collectionFetchController?.abort();
    this.collectionFetchController = null;
  }

  get isDesktop() {
    return this.desktopMedia.matches;
  }

  getCollectionSectionId() {
    if (this.dataset.collectionSectionId) {
      return this.dataset.collectionSectionId;
    }

    const grid = document.getElementById('ProductGridContainer');
    const section = grid?.closest('[id^="shopify-section-"]');
    return section?.id?.replace('shopify-section-', '') || '';
  }

  deleteAllSearchParams(searchParams, key) {
    if (!key) return;
    while (searchParams.has(key)) {
      searchParams.delete(key);
    }
  }

  buildCollectionFilterUrl() {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    this.deleteAllSearchParams(params, this.colorFilterParam);
    this.deleteAllSearchParams(params, this.colorFamilyFilterParam);

    const appendedColorValues = new Set();
    this.getColorInputs()
      .filter((input) => input.checked)
      .forEach((input) => {
        const value = this.getFilterValue(input);
        if (!value || appendedColorValues.has(value)) return;
        appendedColorValues.add(value);
        params.append(this.colorFilterParam, value);
      });

    if (this.activeFamilyValue && this.colorFamilyFilterParam) {
      params.append(this.colorFamilyFilterParam, this.activeFamilyValue);
    }

    params.delete('page');

    return url;
  }

  updateCollectionSections(responseText) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    let updated = false;

    ['ProductGridContainer', 'ProductCount'].forEach((sectionId) => {
      const container = document.getElementById(sectionId);
      const newContent = parsedHTML.getElementById(sectionId);
      if (!container || !newContent) return;

      container.innerHTML = newContent.innerHTML;
      updated = true;
    });

    return updated;
  }

  async applyCollectionFilters() {
    if (!this.colorFilterParam) return;

    const url = this.buildCollectionFilterUrl();
    const checkedColors = this.getColorInputs().filter((input) => input.checked);

    this.logFilterDebug('applyCollectionFilters', {
      url: `${url.pathname}${url.search}`,
      activeFamilyValue: this.activeFamilyValue,
      checkedColorValues: checkedColors.map((input) => input.dataset.filterValue || input.value),
      collectionSectionId: this.getCollectionSectionId()
    });

    const sectionId = this.getCollectionSectionId();

    if (!sectionId) {
      window.location.assign(url.toString());
      return;
    }

    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', sectionId);

    history.replaceState({}, '', url.toString());

    this.collectionFetchController?.abort();
    this.collectionFetchController = new AbortController();

    try {
      const response = await fetch(fetchUrl.toString(), {
        signal: this.collectionFetchController.signal
      });
      const responseText = await response.text();
      const updated = this.updateCollectionSections(responseText);

      if (updated) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText });
        document.dispatchEvent(new CustomEvent('collection:reloaded'));
      } else {
        window.location.assign(url.toString());
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.logFilterDebug('applyCollectionFilters:error', { message: String(error) });
      window.location.assign(url.toString());
    }
  }

  onPopState() {
    if (!this.isFilterMode) return;
    this.syncFromUrl();
  }

  getFilterDebugConfig() {
    const el = document.getElementById(`JumpMenuFilterDebug-${this.dataset.sectionId}`);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return { parseError: String(error) };
    }
  }

  logFilterDebug(step, payload) {
    if (!this.hasAttribute('data-filter-debug')) return;
    const config = this.getFilterDebugConfig();
    console.log(`[jump-menu] ${step}`, { ...payload, storefrontFilters: config });
  }

  getColorInputs(scope = this) {
    return Array.from(scope.querySelectorAll('[data-jump-menu-color-filter]'));
  }

  getFamilyItems() {
    return Array.from(this.querySelectorAll('[data-filter-type="family"]'));
  }

  getAllItem() {
    return this.querySelector('.jump-menu__item--all[data-filter-type="all"]');
  }

  getFilterValue(input) {
    return input?.dataset?.filterValue || input?.value || '';
  }

  syncColorInputState(sourceInput) {
    const value = this.getFilterValue(sourceInput);
    if (!value) return;

    const checked = sourceInput.checked;
    this.getColorInputs().forEach((input) => {
      if (this.getFilterValue(input) === value) {
        input.checked = checked;
      }
    });
  }

  getUniqueCheckedColorCount() {
    const checked = new Set();
    this.getColorInputs().forEach((input) => {
      if (!input.checked) return;
      const value = this.getFilterValue(input);
      if (value) checked.add(value);
    });
    return checked.size;
  }

  updateFamilySelectionCounts() {
    this.getFamilyItems().forEach((item) => {
      const countEl = item.querySelector('[data-jump-menu-family-count]');
      if (!countEl) return;

      const inputs = this.getColorInputs(item);
      const checkedCount = inputs.filter((input) => input.checked).length;

      if (checkedCount === 0) {
        countEl.textContent = '';
        countEl.setAttribute('hidden', '');
      } else {
        countEl.textContent = ` (${checkedCount})`;
        countEl.removeAttribute('hidden');
      }
    });
  }

  updateAllTitleCount() {
    const allItem = this.getAllItem();
    if (!allItem) return;

    const countEl = allItem.querySelector('[data-jump-menu-family-count]');
    if (!countEl) return;

    const checkedCount = this.getUniqueCheckedColorCount();

    if (checkedCount === 0) {
      countEl.textContent = '';
      countEl.setAttribute('hidden', '');
    } else {
      countEl.textContent = ` (${checkedCount})`;
      countEl.removeAttribute('hidden');
    }
  }

  updateFilterUi() {
    if (!this.isFilterMode) return;

    this.items.forEach((item) => this.updateSelectAllState(item));
    this.updateFamilySelectionCounts();
    this.updateAllTitleCount();
    this.updateActiveStates();
  }

  clearTimers() {
    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = null;
    }
  }

  onItemPointerEnter(item) {
    if (!this.isDesktop) return;
    this.positionSubmenu(item);
  }

  positionSubmenusInView() {
    this.querySelectorAll(
      '.jump-menu__item--has-submenu:hover, .jump-menu__item--has-submenu.is-open'
    ).forEach((item) => this.positionSubmenu(item));
  }

  onDesktopChange() {
    this.closeAll();
    this.updateDragAffordance();
    this.refreshArrows();
  }

  onResize() {
    this.updateDragAffordance();
    this.refreshArrows();
    this.positionSubmenusInView();
  }

  onScroll() {
    if (this.scrollRaf) return;

    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null;
      this.positionSubmenusInView();
    });
  }

  setupTileDrag() {
    // A mouse drag reaches tiles past the visible column cap without repeated arrow clicks.
    if (!this.classList.contains('jump-menu--tiles')) return;

    const slider = this.querySelector('slider-element.slider--desktop');
    if (!slider) return;

    this.tileSlider = slider;
    slider.addEventListener('pointerdown', this.onDragPointerDown);
    slider.addEventListener('dragstart', this.onDragNativeStart);
    slider.addEventListener('click', this.onDragClickCapture, true);
    this.updateDragAffordance();

    if (document.readyState !== 'complete') {
      window.addEventListener('load', this.updateDragAffordance, { once: true });
    }
  }

  teardownTileDrag() {
    if (!this.tileSlider) return;

    this.endDrag();
    this.tileSlider.removeEventListener('pointerdown', this.onDragPointerDown);
    this.tileSlider.removeEventListener('dragstart', this.onDragNativeStart);
    this.tileSlider.removeEventListener('click', this.onDragClickCapture, true);
    window.removeEventListener('load', this.updateDragAffordance);
    this.tileSlider = null;
  }

  get isTileSliderScrollable() {
    if (!this.tileSlider) return false;
    return this.tileSlider.scrollWidth - this.tileSlider.clientWidth > 1;
  }

  updateDragAffordance() {
    if (!this.tileSlider) return;
    this.tileSlider.classList.toggle('is-draggable', this.isDesktop && this.isTileSliderScrollable);
  }

  refreshArrows() {
    // slider-element measures arrows on init and scroll only, so recompute after a viewport change.
    this.querySelector('slider-element.slider')?.updateButtons?.();
  }

  onDragPointerDown(event) {
    // Touch and pen pan natively; submenu controls keep their own pointer handling.
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (!this.isDesktop || !this.isTileSliderScrollable) return;
    if (event.target.closest('.jump-menu__submenu')) return;

    this.drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollLeft: this.tileSlider.scrollLeft,
      moved: false
    };

    this.tileSlider.addEventListener('pointermove', this.onDragPointerMove);
    // Capture waits for drag intent, so watch release on the window until then.
    window.addEventListener('pointerup', this.onDragPointerUp, true);
    window.addEventListener('pointercancel', this.onDragPointerUp, true);
  }

  onDragPointerMove(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;

    const distance = event.clientX - this.drag.startX;

    if (!this.drag.moved) {
      if (Math.abs(distance) < 5) return;

      this.drag.moved = true;
      this.tileSlider.classList.add('is-dragging');
      // Close any submenu positioned against the tile the drag is about to move, and clear any text selection.
      this.closeAll();
      document.getSelection()?.removeAllRanges();

      // Capture now that it's a real drag: keeps it alive off the carousel and suppresses hover-open.
      this.tileSlider.setPointerCapture(event.pointerId);
    }

    event.preventDefault();
    this.tileSlider.scrollLeft = this.drag.scrollLeft - distance;
  }

  onDragPointerUp(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;

    const dragged = this.drag.moved;
    this.endDrag(event.pointerId);

    if (!dragged) return;

    // Swallow the click after a drag so releasing over a tile doesn't navigate.
    this.suppressDragClick = true;
    setTimeout(() => {
      this.suppressDragClick = false;
    }, 0);
  }

  endDrag(pointerId = this.drag?.pointerId) {
    if (!this.tileSlider) return;

    this.tileSlider.removeEventListener('pointermove', this.onDragPointerMove);
    window.removeEventListener('pointerup', this.onDragPointerUp, true);
    window.removeEventListener('pointercancel', this.onDragPointerUp, true);
    this.tileSlider.classList.remove('is-dragging');

    if (pointerId != null && this.tileSlider.hasPointerCapture(pointerId)) {
      this.tileSlider.releasePointerCapture(pointerId);
    }

    this.drag = null;
  }

  onDragNativeStart(event) {
    // Keeps the browser's own link/image drag from hijacking a carousel drag.
    if (this.drag) event.preventDefault();
  }

  onDragClickCapture(event) {
    if (!this.suppressDragClick) return;

    this.suppressDragClick = false;
    event.preventDefault();
    event.stopPropagation();
  }

  getItemTrigger(item) {
    return item.querySelector('.jump-menu__item-link');
  }

  handleMobileItemTrigger(event, item) {
    if (this.isDesktop) return;

    const isOpen = item.classList.contains('is-open');

    if (!isOpen) {
      event.preventDefault();
      this.clearTimers();
      this.openItemPanel(item);
      return;
    }

    event.preventDefault();
    this.closeItem(item);
  }

  onItemTriggerClick(event, item) {
    // Authored override links navigate natively, including on touch devices.
    if (item.hasAttribute('data-override-link')) return;

    if (this.hasAttribute('data-collection-filter-links')) {
      if (!this.isDesktop && item.hasAttribute('data-has-submenu')) {
        event.preventDefault();
        this.handleMobileItemTrigger(event, item);
        return;
      }
      return;
    }

    if (this.isFilterMode && item.dataset.filterType === 'all') {
      event.preventDefault();
      if (this.isDesktop) {
        this.openItemPanel(item);
        return;
      }
      this.handleMobileItemTrigger(event, item);
      return;
    }

    if (this.isFilterMode && item.dataset.filterType === 'family') {
      this.onFilterFamilyClick(event, item);
      return;
    }

    this.handleMobileItemTrigger(event, item);
  }

  onItemTriggerKeydown(event, item) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    // Authored override links navigate natively; don't intercept keyboard activation.
    if (item.hasAttribute('data-override-link')) return;

    if (this.hasAttribute('data-collection-filter-links')) {
      if (item.hasAttribute('data-has-submenu')) {
        event.preventDefault();

        if (!this.isDesktop) {
          this.handleMobileItemTrigger(event, item);
          return;
        }

        // Desktop opens this submenu on hover; keyboard users need Enter/Space to reach it.
        if (this.openItem === item) {
          this.closeItem(item);
        } else {
          this.clearTimers();
          this.openItemPanel(item);
        }
        return;
      }
      return;
    }

    if (this.isFilterMode && item.dataset.filterType === 'all') {
      event.preventDefault();
      if (this.isDesktop) {
        this.openItemPanel(item);
        return;
      }
      this.handleMobileItemTrigger(event, item);
      return;
    }

    if (this.isFilterMode && item.dataset.filterType === 'family') {
      this.onFilterFamilyClick(event, item);
      return;
    }

    if (this.isDesktop) return;

    this.handleMobileItemTrigger(event, item);
  }

  onFilterChange(event) {
    if (!this.isFilterMode) return;

    this.syncColorInputState(event.target);
    this.clearFamilyFilter();
    this.updateFilterUi();
    this.applyCollectionFilters();
  }

  onSelectAllChange(event) {
    if (!this.isFilterMode) return;

    const item = event.target.closest('.jump-menu__item[data-has-submenu]');
    if (!item) return;

    this.clearFamilyFilter();

    const shouldCheck = event.target.checked;

    if (item.dataset.filterType === 'all') {
      this.getColorInputs().forEach((input) => {
        input.checked = shouldCheck;
      });
    } else {
      this.getColorInputs(item).forEach((input) => {
        input.checked = shouldCheck;
        this.syncColorInputState(input);
      });
    }

    this.updateFilterUi();
    this.applyCollectionFilters();
  }

  clearFamilyFilter() {
    this.activeFamilyValue = null;
    this.getFamilyItems().forEach((item) => {
      item.classList.remove('is-family-filter-active');
    });
  }

  onFilterFamilySelectAllClick(event, item) {
    if (!this.isFilterMode) return;

    event.preventDefault();
    this.clearFamilyFilter();

    const inputs = this.getColorInputs(item);
    const allChecked = inputs.length > 0 && inputs.every((input) => input.checked);
    const shouldCheck = !allChecked;

    inputs.forEach((input) => {
      input.checked = shouldCheck;
      this.syncColorInputState(input);
    });

    const selectAll = item.querySelector('[data-jump-menu-select-all]');
    if (selectAll) {
      selectAll.checked = shouldCheck;
      selectAll.indeterminate = false;
    }

    this.openItemPanel(item);
    this.updateFilterUi();
    this.applyCollectionFilters();
  }

  onFilterFamilyClick(event, item) {
    if (!this.isFilterMode) return;

    event.preventDefault();

    if (item.hasAttribute('data-has-submenu')) {
      if (this.isDesktop) {
        this.onFilterFamilySelectAllClick(event, item);
        return;
      }

      this.handleMobileItemTrigger(event, item);
      return;
    }

    const familyValue = item.dataset.filterValue;
    if (!familyValue) return;

    const isActive = item.classList.contains('is-family-filter-active');

    this.clearFamilyFilter();
    this.getColorInputs().forEach((input) => {
      input.checked = false;
    });
    this.items.forEach((menuItem) => this.updateSelectAllState(menuItem));

    if (!isActive) {
      item.classList.add('is-family-filter-active');
      this.activeFamilyValue = familyValue;
    }

    this.updateFilterUi();
    this.applyCollectionFilters();
  }

  onFacetUpdate() {
    if (!this.isFilterMode) return;
    this.syncFromUrl();
  }

  syncFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const activeColorValues = params.getAll(this.colorFilterParam);
    const activeFamilyValues = this.colorFamilyFilterParam
      ? params.getAll(this.colorFamilyFilterParam)
      : [];

    this.activeFamilyValue = activeFamilyValues[0] || null;

    this.getColorInputs().forEach((input) => {
      const value = this.getFilterValue(input);
      input.checked = activeColorValues.includes(value);
    });

    this.getFamilyItems().forEach((item) => {
      item.classList.toggle(
        'is-family-filter-active',
        Boolean(this.activeFamilyValue) && item.dataset.filterValue === this.activeFamilyValue
      );
    });

    this.updateFilterUi();
  }

  updateSelectAllState(item) {
    const selectAll = item.querySelector('[data-jump-menu-select-all]');
    if (!selectAll) return;

    const inputs = this.getColorInputs(item);
    const checkedInputs = inputs.filter((input) => input.checked);

    selectAll.checked = inputs.length > 0 && checkedInputs.length === inputs.length;
    selectAll.indeterminate = checkedInputs.length > 0 && checkedInputs.length < inputs.length;
  }

  updateActiveStates() {
    if (!this.isFilterMode) return;

    this.getFamilyItems().forEach((item) => {
      const hasActiveChild = this.getColorInputs(item).some((input) => input.checked);
      const hasActiveFamily = item.classList.contains('is-family-filter-active');
      item.classList.toggle('is-filter-active', hasActiveChild || hasActiveFamily);
    });

    const allItem = this.getAllItem();
    if (allItem) {
      allItem.classList.toggle('is-filter-active', this.getUniqueCheckedColorCount() > 0);
    }
  }

  openItemPanel(item) {
    const submenu = item.querySelector('.jump-menu__submenu');
    const trigger = this.getItemTrigger(item);
    if (!submenu) return;

    this.clearTimers();

    if (this.openItem && this.openItem !== item) {
      this.closeItem(this.openItem, false);
    }

    this.openItem = item;
    item.classList.add('is-open');
    submenu.classList.remove('is-offset-left', 'is-offset-right');

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    this.positionSubmenu(item);
    requestAnimationFrame(() => {
      if (this.openItem === item) {
        this.positionSubmenu(item);
      }
    });
  }

  closeItem(item, clearOpenRef = true) {
    const submenu = item.querySelector('.jump-menu__submenu');
    const trigger = this.getItemTrigger(item);

    item.classList.remove('is-open');
    if (submenu) {
      submenu.classList.remove('is-offset-left', 'is-offset-right');
    }
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }
    if (clearOpenRef && this.openItem === item) {
      this.openItem = null;
    }
  }

  closeAll() {
    this.clearTimers();
    this.items.forEach((item) => this.closeItem(item));
  }

  positionSubmenu(item) {
    const submenu = item.querySelector('.jump-menu__submenu');
    if (!submenu) return;

    const itemRect = item.getBoundingClientRect();
    const sectionRoot = this.closest('.shopify-section') || this;
    const sectionRect = sectionRoot.getBoundingClientRect();
    const gap = 4;

    submenu.style.setProperty('--jump-menu-submenu-gap', `${gap}px`);
    submenu.style.position = 'fixed';
    submenu.style.top = `${itemRect.bottom + gap}px`;
    submenu.style.marginLeft = '0';
    submenu.style.width = 'fit-content';
    submenu.style.maxWidth = `${Math.min(320, sectionRect.width - 16, window.innerWidth - 16)}px`;

    const submenuRect = submenu.getBoundingClientRect();
    let left = itemRect.left + itemRect.width / 2 - submenuRect.width / 2;

    const minLeft = sectionRect.left + 8;
    const maxLeft = sectionRect.right - submenuRect.width - 8;

    submenu.classList.remove('is-offset-left', 'is-offset-right');

    if (left < minLeft) {
      left = minLeft;
      submenu.classList.add('is-offset-left');
    } else if (left > maxLeft) {
      left = maxLeft;
      submenu.classList.add('is-offset-right');
    }

    submenu.style.left = `${left}px`;
    submenu.style.right = 'auto';
    submenu.style.transform = 'none';
  }

  onDocumentClick(event) {
    if (!this.openItem) return;
    if (this.openItem.contains(event.target)) return;
    this.closeAll();
  }

  onDocumentKeydown(event) {
    if (event.key === 'Escape' && this.openItem) {
      event.preventDefault();
      const item = this.openItem;
      this.closeAll();
      const trigger = this.getItemTrigger(item);
      if (trigger) trigger.focus();
    }
  }

  onSubmenuKeydown(event, item) {
    if (this.isFilterMode) {
      const inputs = [
        item.querySelector('[data-jump-menu-select-all]'),
        ...this.getColorInputs(item)
      ].filter(Boolean);
      if (!inputs.length) return;

      const currentIndex = inputs.indexOf(document.activeElement);

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        const next = inputs[(currentIndex + 1) % inputs.length] || inputs[0];
        next.focus();
      }

      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        const prev = inputs[(currentIndex - 1 + inputs.length) % inputs.length] || inputs[inputs.length - 1];
        prev.focus();
      }

      return;
    }

    const links = Array.from(item.querySelectorAll('.jump-menu__submenu a[href]'));
    if (!links.length) return;

    const currentIndex = links.indexOf(document.activeElement);

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      const next = links[(currentIndex + 1) % links.length] || links[0];
      next.focus();
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      const prev = links[(currentIndex - 1 + links.length) % links.length] || links[links.length - 1];
      prev.focus();
    }
  }

  onBlockSelect(event) {
    const blockId = event.detail?.blockId;
    if (!blockId) return;

    const item = this.querySelector(`[data-shopify-editor-block*="${blockId}"]`)?.closest('.jump-menu__item[data-has-submenu]')
      || this.querySelector(`#shopify-block-${blockId}`)?.closest('.jump-menu__item[data-has-submenu]');

    if (item) {
      this.clearTimers();
      this.openItemPanel(item);
    }
  }
}

customElements.define('jump-menu-section', JumpMenuSection);
})();
